import { useRef, useEffect } from 'react'
import './WorkWindow.css'
import { EDUCATION, EXPERIENCE, PROJECTS, RESEARCH, CERTIFICATIONS, SKILLS, SERVICES } from '@/data/work'

const CANVAS_SIZE = 180

// Animation functions
function setup3DSphereScan(ctx: CanvasRenderingContext2D) {
  let time = 0
  const centerX = CANVAS_SIZE / 2, centerY = CANVAS_SIZE / 2
  const radius = CANVAS_SIZE * 0.4, numDots = 200
  const dots: { x: number; y: number; z: number }[] = []
  for (let i = 0; i < numDots; i++) {
    const theta = Math.acos(1 - 2 * (i / numDots))
    const phi = Math.sqrt(numDots * Math.PI) * theta
    dots.push({ x: radius * Math.sin(theta) * Math.cos(phi), y: radius * Math.sin(theta) * Math.sin(phi), z: radius * Math.cos(theta) })
  }
  function animate() {
    time += 0.008
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE)
    const rotX = Math.sin(time * 0.3) * 0.5, rotY = time * 0.5
    const scanLine = (Math.sin(time * 2.5) * 0.5 + 0.5) * 2 * radius - radius
    dots.forEach((dot) => {
      let { x, y, z } = dot
      let nX = x * Math.cos(rotY) - z * Math.sin(rotY); let nZ = x * Math.sin(rotY) + z * Math.cos(rotY); x = nX; z = nZ
      let nY = y * Math.cos(rotX) - z * Math.sin(rotX); nZ = y * Math.sin(rotX) + z * Math.cos(rotX); y = nY; z = nZ
      const scale = (z + radius * 1.5) / (radius * 2.5), pX = centerX + x, pY = centerY + y
      const distToScan = Math.abs(y - scanLine), scanInfluence = distToScan < 25 ? Math.cos((distToScan / 25) * (Math.PI / 2)) : 0
      const size = Math.max(0, scale * 2.0 + scanInfluence * 2.5), opacity = Math.max(0, scale * 0.6 + scanInfluence * 0.4)
      ctx.beginPath(); ctx.arc(pX, pY, size, 0, Math.PI * 2); ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`; ctx.fill()
    })
    requestAnimationFrame(animate)
  }
  requestAnimationFrame(animate)
}

function setupHelixScanner(ctx: CanvasRenderingContext2D) {
  let time = 0
  const centerX = CANVAS_SIZE / 2, centerY = CANVAS_SIZE / 2, numDots = 80, radius = 30, height = 100
  const dots: { angle: number; y: number }[] = []
  for (let i = 0; i < numDots; i++) dots.push({ angle: i * 0.3, y: (i / numDots) * height - height / 2 })
  function animate() {
    time += 0.008; ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE)
    const scanY = Math.sin(time * 2) * (height / 2)
    dots.forEach((dot) => {
      const x = radius * Math.cos(dot.angle + time), z = radius * Math.sin(dot.angle + time)
      const pX = centerX + x, pY = centerY + dot.y, scale = (z + radius) / (radius * 2)
      const distToScan = Math.abs(dot.y - scanY), scanInfluence = distToScan < 25 ? Math.cos((distToScan / 25) * (Math.PI / 2)) : 0
      const size = Math.max(0, scale * 1.8 + scanInfluence * 2.5), opacity = Math.max(0, scale * 0.4 + scanInfluence * 0.6)
      ctx.beginPath(); ctx.arc(pX, pY, size, 0, Math.PI * 2); ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`; ctx.fill()
    })
    requestAnimationFrame(animate)
  }
  requestAnimationFrame(animate)
}

function setupSonarSweep(ctx: CanvasRenderingContext2D) {
  let time = 0
  const centerX = CANVAS_SIZE / 2, centerY = CANVAS_SIZE / 2
  function animate() {
    time += 0.015; ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE)
    const sweepAngle = (time * 2) % (Math.PI * 2)
    for (let r = 20; r < CANVAS_SIZE / 2; r += 20) {
      ctx.beginPath(); ctx.arc(centerX, centerY, r, 0, Math.PI * 2)
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)'; ctx.stroke()
    }
    ctx.beginPath(); ctx.moveTo(centerX, centerY)
    ctx.arc(centerX, centerY, CANVAS_SIZE / 2 - 10, sweepAngle, sweepAngle + 0.5)
    ctx.lineTo(centerX, centerY)
    const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, CANVAS_SIZE / 2)
    gradient.addColorStop(0, 'rgba(255,255,255,0.6)'); gradient.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = gradient; ctx.fill()
    requestAnimationFrame(animate)
  }
  requestAnimationFrame(animate)
}

function setupVoxelMatrix(ctx: CanvasRenderingContext2D) {
  let time = 0
  const gridSize = 6, spacing = CANVAS_SIZE / (gridSize + 1)
  function animate() {
    time += 0.02; ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE)
    for (let x = 0; x < gridSize; x++) {
      for (let y = 0; y < gridSize; y++) {
        const px = spacing + x * spacing, py = spacing + y * spacing
        const wave = Math.sin(time + x * 0.5 + y * 0.3) * 0.5 + 0.5
        const size = 4 + wave * 8, opacity = 0.3 + wave * 0.5
        ctx.beginPath(); ctx.rect(px - size/2, py - size/2, size, size)
        ctx.fillStyle = `rgba(255,255,255,${opacity})`; ctx.fill()
      }
    }
    requestAnimationFrame(animate)
  }
  requestAnimationFrame(animate)
}

// Corner Plus SVG
const CornerPlus = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 0V16M0 8H16" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
)

// Animation Box Component with corner plus signs (no title)
function AnimationBox({ canvasRef }: { canvasRef: React.RefObject<HTMLCanvasElement> }) {
  return (
    <div className="work-window__animation-box">
      <div className="corner top-left"><CornerPlus /></div>
      <div className="corner top-right"><CornerPlus /></div>
      <div className="corner bottom-left"><CornerPlus /></div>
      <div className="corner bottom-right"><CornerPlus /></div>
      <canvas ref={canvasRef} width={CANVAS_SIZE} height={CANVAS_SIZE} />
    </div>
  )
}

// Empty spacer for diagonal pattern
function AnimationSpacer() {
  return <div className="work-window__animation-spacer" />
}

export function WorkWindow() {
  const sphereRef = useRef<HTMLCanvasElement>(null)
  const helixRef = useRef<HTMLCanvasElement>(null)
  const sonarRef = useRef<HTMLCanvasElement>(null)
  const voxelRef = useRef<HTMLCanvasElement>(null)
  const sphere2Ref = useRef<HTMLCanvasElement>(null)
  const helix2Ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (sphereRef.current) { const ctx = sphereRef.current.getContext('2d'); if (ctx) setup3DSphereScan(ctx) }
    if (helixRef.current) { const ctx = helixRef.current.getContext('2d'); if (ctx) setupHelixScanner(ctx) }
    if (sonarRef.current) { const ctx = sonarRef.current.getContext('2d'); if (ctx) setupSonarSweep(ctx) }
    if (voxelRef.current) { const ctx = voxelRef.current.getContext('2d'); if (ctx) setupVoxelMatrix(ctx) }
    if (sphere2Ref.current) { const ctx = sphere2Ref.current.getContext('2d'); if (ctx) setup3DSphereScan(ctx) }
    if (helix2Ref.current) { const ctx = helix2Ref.current.getContext('2d'); if (ctx) setupHelixScanner(ctx) }
  }, [])

  return (
    <div className="work-window">
      <div className="work-window__container">
        <div className="work-window__hero">
        <h1 className="work-window__title">WORK</h1>
        <p className="work-window__subtitle">AI ENGINEERING & CREATIVE DEVELOPMENT</p>
      </div>

      <div className="work-window__columns">
        <div className="work-window__column">
          <div className="work-window__section">
            <h2 className="work-window__section-title">+EDUCATION</h2>
            <table className="work-window__table">
              <tbody>{EDUCATION.map((e, i) => <tr key={i}><td>{e.degree}</td><td>{e.institution}</td><td className="work-window__table-year">{e.year}</td></tr>)}</tbody>
            </table>
          </div>

          <div className="work-window__section">
            <h2 className="work-window__section-title">+EXPERIENCE</h2>
            <table className="work-window__table">
              <tbody>{EXPERIENCE.map((e, i) => <tr key={i}><td>{e.role}</td><td>{e.company}</td><td className="work-window__table-year">{e.period}</td></tr>)}</tbody>
            </table>
          </div>

          <div className="work-window__section">
            <h2 className="work-window__section-title">+PROJECTS</h2>
            <table className="work-window__table">
              <tbody>{PROJECTS.map((p, i) => <tr key={i}><td>{p.name}</td><td>{p.tech}</td><td className="work-window__table-status">{p.status}</td></tr>)}</tbody>
            </table>
          </div>

          <div className="work-window__section">
            <h2 className="work-window__section-title">+RESEARCH</h2>
            <table className="work-window__table">
              <tbody>{RESEARCH.map((r, i) => <tr key={i}><td>{r.title}</td><td>{r.venue}</td><td className="work-window__table-year">{r.year}</td></tr>)}</tbody>
            </table>
          </div>

          <div className="work-window__section">
            <h2 className="work-window__section-title">+CERTIFICATIONS</h2>
            <table className="work-window__table">
              <tbody>{CERTIFICATIONS.map((c, i) => <tr key={i}><td>{c.name}</td><td>{c.issuer}</td><td className="work-window__table-year">{c.year}</td></tr>)}</tbody>
            </table>
          </div>
            <div className="work-window__section">
              <h2 className="work-window__section-title">+SKILLS</h2>
              <div className="work-window__skills">{SKILLS.map((s, i) => <span key={i} className="work-window__skill">{s}</span>)}</div>
            </div>        </div>

        <div className="work-window__column">
          {/* Diagonal pattern: animation, spacer, spacer, animation alternating */}
          <div className="work-window__animations">
            <AnimationBox canvasRef={sphereRef} />
            <AnimationSpacer />
            <AnimationSpacer />
            <AnimationBox canvasRef={helixRef} />
            <AnimationBox canvasRef={sonarRef} />
            <AnimationSpacer />
            <AnimationSpacer />
            <AnimationBox canvasRef={voxelRef} />
            <AnimationBox canvasRef={sphere2Ref} />
            <AnimationSpacer />
            <AnimationSpacer />
            <AnimationBox canvasRef={helix2Ref} />
          </div>
        </div>
      </div>

      <div className="work-window__services-section">
        <h2 className="work-window__section-title">+SERVICES</h2>
        <div className="work-window__services">{SERVICES.map((s, i) => <div key={i} className="work-window__service-item"><h3>{s.title}</h3><p>{s.desc}</p></div>)}</div>
      </div>
      </div>
    </div>
  )
}
